import FirstPersonTool from "../../tools/first-person-tool";
import HotkeyManager, { KeyMapping } from "../../managers/hotkey-manager";
import OrbitRetargetTool from "../../tools/orbit-retarget-tool";
import OrbitTool from "../../tools/new-orbit-tool";
import Overhead3DMovementTool from "../../tools/overhead-3d-movement-tool";
import Signal from "../../control-flow/signal";
import getLogger from '../../logging';
import lookAtGap from "../../view3d/look-at-gap";
import lookAtWall from "../../view3d/look-at-wall";
import { CameraConfig, cameraDefaults, SavedViewConfig } from "../../views/views-config";
import { PerspectiveCamera, Scene } from "three";
import { QUARTER_TURN } from "../../math/constants";
import { Wall } from "../../domain";
import { applySavedViewCamera, extractSavedViewCamera } from "../../controllers/saved-view-controllers";
import { clone, update } from "../../json/optional-properties";
import { findRoomCenter } from "../../controllers/room-controllers";
import { onChange as onAnimatePoseChange } from '../../animation/animate-pose';

const log = getLogger('walk-in');

export type NavigationMode = "orbit" | "fp" | "overhead";

const MODE_STORAGE_KEY = 'cps-walk-in-camera-mode';

// How much extra space to add when pointing the camera at a wall
const FRAMING_MARGIN = 12;

export const setSavedMode = (mode: NavigationMode) => {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
};

export const getSavedMode = (): NavigationMode => {
    return localStorage.getItem(MODE_STORAGE_KEY) as NavigationMode ?? "fp";
};


export class WalkInCameraManager {
    camera: PerspectiveCamera;

    mode: NavigationMode;
    lastMode: NavigationMode;

    tools: {
        orbit: OrbitTool,
        retarget: OrbitRetargetTool,
        fp: FirstPersonTool,
        overhead: Overhead3DMovementTool,
    };
    overheadCameraConfig: CameraConfig;
    normalCameraConfig: CameraConfig;

    onUpdate = new Signal('onUpdate', 'walk-in');
    onModeSwitch = new Signal('onModeSwitch', 'walk-in');

    constructor(camera: PerspectiveCamera) {
        this.camera = camera;
    }

    setup(defaultView: SavedViewConfig, walls: Wall[], canvas: HTMLCanvasElement, scene: Scene) {
        onAnimatePoseChange.add(this.onUpdate.trigger);
        
        const hotkeys = HotkeyManager.getInstance();
        hotkeys.on('toggle-overhead-view', this.toggleOverhead);


        // Note that the orbit tool's presets can move the camera, so it's
        // best to do this prior to setting up the initial camera positions
        // for the nav modes. Ultimately we'll start with the first person
        // controls, which will not move the camera implicitly.
        const orbit = new OrbitTool(this.camera, canvas);
        orbit.applyPreset("free");

        orbit.onChange.add(this.onUpdate.trigger);

        const retarget = new OrbitRetargetTool(orbit, scene);
        retarget.onChange.add(this.onUpdate.trigger);

        const fp = new FirstPersonTool(this.camera, canvas);
        fp.onChange.add(this.onUpdate.trigger);

        const overhead = new Overhead3DMovementTool(this.camera, canvas);
        overhead.onChange.add(this.onUpdate.trigger);

        this.tools = { orbit, retarget, fp, overhead };

        this.normalCameraConfig = clone(cameraDefaults);

        const [x, z] = findRoomCenter(walls);
        this.overheadCameraConfig = clone(cameraDefaults);
        update(this.overheadCameraConfig, {
            position: {x, y: 240, z},
            rotation: {x: -QUARTER_TURN, y: 0, z: 0},
        });

        // If we don't have any saved views then the default view will have a
        // null name. In this case we place the camera between the first and
        // last walls.
        if(defaultView.name == null) {
            const nonIslands = walls.filter(wall => !wall.island);
            switch(nonIslands.length) {
                case 0:
                    log.error('Unable to place default camera for a room with no walls');
                    break;
                case 1:
                    lookAtWall(this.camera, nonIslands[0], FRAMING_MARGIN, orbit.target);
                    this.tools.orbit.controls.update();
                    break;
                default: {
                    const
                        first = nonIslands[0],
                        last = nonIslands[nonIslands.length - 1];
                    lookAtGap(this.camera, first, last, FRAMING_MARGIN, orbit.target);
                    this.tools.orbit.controls.update();
                }

            }
        }

        this.switchMode(getSavedMode());
    }

    switchMode(newmode: NavigationMode) {
        // we ignore trivial switches to the same mode
        if(this.mode == newmode) return;

        log.debug('switchMode() switching to ' + newmode);

        // keep track of the mode we switched from
        this.lastMode = this.mode;
        this.mode = newmode;

        switch(this.lastMode) {
            case "orbit":
                this.normalCameraConfig =
                    extractSavedViewCamera(this.camera);
                break;
            case "fp":
                this.normalCameraConfig = extractSavedViewCamera(this.camera);
                break;
            case "overhead":
                this.overheadCameraConfig = extractSavedViewCamera(this.camera);
                break;
            default:
                // when lastMode is undefined, most likely because we were
                // called at the end of the setup method.
                this.normalCameraConfig = extractSavedViewCamera(this.camera);
        }
        switch(newmode) {
            case "orbit":
                applySavedViewCamera(this.normalCameraConfig, this.camera)
                    .finally(() => {
                        // Reset the target point 96" (8 feet) in front of the camera
                        const target = this.tools.orbit.target;
                        target.set(0, 0, -96);
                        this.camera.localToWorld(target);
                        this.tools.orbit.controls.update();

                        this.tools.fp.disable();
                        this.tools.overhead.disable();
                        this.tools.orbit.enable(); // <- enabled
                        this.tools.retarget.enable(); // <- enabled
                    });
                setSavedMode("orbit");
                break;
            case "fp":
                applySavedViewCamera(this.normalCameraConfig, this.camera)
                    .finally(() => {
                        this.tools.orbit.disable();
                        this.tools.retarget.disable();
                        this.tools.fp.enable(); // <- enabled
                        this.tools.overhead.disable();
                    });
                setSavedMode("fp");
                break;
            case "overhead":
                applySavedViewCamera(this.overheadCameraConfig, this.camera)
                    .finally(() => {
                        this.tools.orbit.disable();
                        this.tools.retarget.disable();
                        this.tools.fp.disable();
                        this.tools.overhead.enable(); // <- enabled
                    });
                // Note that "overhead" is not a saved state
                break;
        }

        this.onModeSwitch.trigger();
        this.onUpdate.trigger();
    }

    toggleOverhead = (_event: KeyboardEvent) => {
        if(this.mode == 'overhead') {
            this.switchMode(this.lastMode);
        } else {
            this.switchMode('overhead');
        }
    };

}
